import './hero.scss'

import {ArrowIcon, HelpIcon} from 'styles/inline-icons'
import {
	HeroType,
	ImageType,
	LinkBaseType,
	LinkType,
	ThanksData,
	WysiwygType
} from 'campagne/types'
import {Mod, style} from 'util/campagne/style'
import React, {FunctionComponent, useState} from 'react'

import {Background} from '@codeurs/front'
import {Icon} from '../../ui/Icon'
import {Link} from '../../../util/link'
import {Theme} from 'views/campagne/theme'
import queryString from 'query-string'
import {useTranslation} from 'util/i18n'

const styles = style('hero', ['image', 'texts', 'cta', 'help'])

export const Hero: FunctionComponent<HeroType> = ({image, help, ...text}) => {
	const t = useTranslation('campagne').hero
	const projectKey = window['__INITIAL_STATE__'].project_key

	return (
		<div class={styles}>
			<Theme.Container>
				<HeroText {...text} />
			</Theme.Container>
			<HeroLine />
			<div class={styles('image')}>{image && <HeroImage {...image} />}</div>
			{projectKey !== 'broederlijkdelen' && (
				<HeroCta
					url="https://www.broederlijkdelen.be"
					title="Broederlijk Delen"
				/>
			)}
			{help && <HeroCta url={help.url} title={t.help} mod="inverted" />}
		</div>
	)
}
export const HeroSmall: FunctionComponent<
	HeroType & {
		registerPage?: boolean
		actionPage?: boolean
		actionsPage?: boolean
		defaultHero?: ImageType
		inspirationImages?: Array<ImageType>
		thanks?: ThanksData
		previous?: LinkType
	}
> = ({
	image,
	registerPage,
	actionPage,
	actionsPage,
	defaultHero,
	inspirationImages,
	thanks,
	previous,
	help,
	...text
}) => {
	const t = useTranslation('campagne').hero
	const projectKey = window['__INITIAL_STATE__'].project_key
	const heroImage = registerPage
		? getHeroFromParameter({defaultHero, inspirationImages})
		: image
	const params = queryString.parse(location.search)
	if (actionPage && params.action === 'thanks') {
		text.title = thanks.title
	}
	return (
		<div key={heroImage?.src} class={styles.mod('small')}>
			<Theme.Container>
				{previous && (
					<div class={styles('back')}>
						<div class={styles('back-link')}>
							<ArrowIcon className={styles('back-link-arrow')} />
							<Theme.Link to={previous.url}>{previous.description}</Theme.Link>
						</div>
					</div>
				)}
				<HeroText mod={'wide'} bigText={actionsPage} {...text} />
			</Theme.Container>
			{heroImage && (
				<div class={styles('image')}>
					<HeroImage {...heroImage} />
				</div>
			)}
			{projectKey !== 'broederlijkdelen' && (
				<HeroCta
					url="https://www.broederlijkdelen.be"
					title="Broederlijk Delen"
				/>
			)}
			{help && (
				<HeroCta url={help.url} title={help.description} mod="inverted" />
			)}
		</div>
	)
}
export const getHeroFromParameter = ({
	defaultHero,
	inspirationImages
}): ImageType => {
	const url = new URL(window.location.href)
	const c = url.searchParams.get('inspiratie')
	const heroImage = inspirationImages.find((img) => img.path === c)
	if (heroImage) return heroImage.image
	return defaultHero
}

export const HeroLine: FunctionComponent = () => (
	<div class={styles('triangle')} />
)

export const HeroImage: FunctionComponent<ImageType> = (image) => {
	const [isLoaded, setLoaded] = useState(false)
	return (
		<div class={styles.image.is({loaded: isLoaded}).toString()}>
			<Background
				img={image.src}
				className={styles.image('bg').toString()}
				onload={() => setLoaded(true)}
			/>
		</div>
	)
}

export const HeroText: FunctionComponent<{
	title?: string
	subtitle?: string
	intro?: WysiwygType
	link?: LinkBaseType
	mod?: Mod<'wide'>
	bigText?: boolean
}> = ({title, subtitle, intro, link, bigText, mod}) => (
	<div className={styles.texts.mod(mod)}>
		{title && (
			<Theme.H2
				as="h1"
				mod={['green', 'marked', bigText ? 'big' : null]}
				className={styles.texts('title')}
			>
				{title}
			</Theme.H2>
		)}
		{subtitle && (
			<div class={styles.texts('subtitle')}>
				<div class={styles.texts('subtitle-title')}>{subtitle}</div>
			</div>
		)}
		{intro && (
			<div className={styles.texts('intro')}>
				<Theme.Wysiwyg>{intro}</Theme.Wysiwyg>
			</div>
		)}
		{link && link.url && (
			<div className={styles.texts('link')}>
				<Theme.Link to={link.url} withArrow={true}>
					{link.description}
				</Theme.Link>
			</div>
		)}
	</div>
)

export const HeroHelp: FunctionComponent = ({children}) => (
	<span class={styles.help}>
		<HelpIcon class={styles.help('icon').toString()} />
		{children}
	</span>
)

export const HeroCta: React.FC<{
	url: string
	title?: string
	mod?: Mod<'inverted'>
}> = ({url, title, mod}) =>
	url && (
		<Link to={url} className={styles.cta().mod(mod)}>
			<span className={styles.cta('icon')}>
				<Icon icon={mod === 'inverted' ? 'question' : 'compass'} />
			</span>
			<span className={styles.cta('title')}>{title || url}</span>
		</Link>
	)
